import React, { useEffect, useRef, useState } from "react";
import { Camera, Upload, X, RotateCcw, Check } from "lucide-react";

/**
 * Two-button photo capture (v2.3 §8.1 fix):
 *  - "Take photo" → opens LIVE camera via getUserMedia (webcam on desktop, rear cam on mobile)
 *      with explicit "Capture" button + retake. Falls back to capture="environment" file input if getUserMedia unavailable.
 *  - "Upload photo" → standard file picker.
 * Calls onChange(dataUrl) with a base64 data URL.
 */
export default function PhotoCapture({ value, onChange, testidPrefix = "photo", height = 220 }) {
  const uplRef = useRef();
  const camRef = useRef();  // hidden fallback file input with capture
  const [cam, setCam] = useState(false);
  const [err, setErr] = useState("");

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => onChange?.(r.result);
    r.readAsDataURL(f);
  };

  const openCamera = async () => {
    setErr("");
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      setCam(true);
    } else {
      // Fallback: trigger the hidden capture-mode file input
      camRef.current?.click();
    }
  };

  return (
    <div data-testid={`${testidPrefix}-capture`}>
      <input ref={uplRef} type="file" accept="image/*" onChange={handleFile} style={{ display: "none" }} data-testid={`${testidPrefix}-upload-input`} />
      <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={handleFile} style={{ display: "none" }} data-testid={`${testidPrefix}-camera-input`} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <button type="button" className="btn-primary" style={{ height: 52 }} onClick={openCamera} data-testid={`${testidPrefix}-take-btn`}>
          <Camera size={18} style={{ marginRight: 8, verticalAlign: -3 }} /> Take photo
        </button>
        <button type="button" className="btn-ghost" style={{ height: 52, fontWeight: 700 }} onClick={() => uplRef.current?.click()} data-testid={`${testidPrefix}-upload-btn`}>
          <Upload size={16} style={{ marginRight: 8, verticalAlign: -3 }} /> Upload photo
        </button>
      </div>
      {err ? <div className="badge badge-amber" style={{ padding: "4px 8px", marginTop: 6 }}>{err}</div> : null}
      {value ? (
        <img
          alt="capture preview"
          src={value}
          data-testid={`${testidPrefix}-preview`}
          style={{ width: "100%", marginTop: 10, maxHeight: height, objectFit: "cover", border: "1px solid #cbd5e1" }}
        />
      ) : null}
      {cam ? (
        <CameraModal
          onClose={() => setCam(false)}
          onCaptured={(dataUrl) => { onChange?.(dataUrl); setCam(false); }}
          onError={(m) => { setErr(m); setCam(false); }}
          testidPrefix={testidPrefix}
        />
      ) : null}
    </div>
  );
}

function CameraModal({ onClose, onCaptured, onError, testidPrefix }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [snapshot, setSnapshot] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (!mounted) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
          setReady(true);
        }
      } catch (e) {
        onError?.(e.message || "Camera access denied / unavailable");
      }
    };
    start();
    return () => {
      mounted = false;
      const s = streamRef.current;
      if (s) s.getTracks().forEach((t) => t.stop());
    };
  }, [onError]);

  const snap = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    const w = v.videoWidth || 1280;
    const h = v.videoHeight || 720;
    c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0, w, h);
    setSnapshot(c.toDataURL("image/jpeg", 0.85));
  };

  const confirm = () => {
    if (snapshot) onCaptured?.(snapshot);
  };

  return (
    <div data-testid={`${testidPrefix}-camera-modal`} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 200, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <button type="button" onClick={onClose} className="btn-ghost" style={{ position: "absolute", top: 16, right: 16, color: "white", borderColor: "#475569", background: "transparent" }} data-testid={`${testidPrefix}-camera-close`}>
        <X size={18} />
      </button>
      <div style={{ width: "100%", maxWidth: 720, background: "#000", border: "1px solid #1e293b" }}>
        {!snapshot ? (
          <video ref={videoRef} playsInline muted autoPlay style={{ width: "100%", display: "block" }} data-testid={`${testidPrefix}-camera-video`} />
        ) : (
          <img src={snapshot} alt="snapshot preview" style={{ width: "100%", display: "block" }} data-testid={`${testidPrefix}-camera-snapshot`} />
        )}
        <canvas ref={canvasRef} style={{ display: "none" }} />
      </div>
      <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
        {!snapshot ? (
          <button type="button" className="btn-primary" disabled={!ready} onClick={snap} style={{ height: 56, minWidth: 200, fontSize: 16, fontWeight: 800 }} data-testid={`${testidPrefix}-camera-capture`}>
            <Camera size={18} style={{ marginRight: 8, verticalAlign: -3 }} /> {ready ? "Capture" : "Starting camera…"}
          </button>
        ) : (
          <>
            <button type="button" className="btn-ghost" onClick={() => setSnapshot("")} style={{ height: 56, minWidth: 140, color: "white", borderColor: "#475569" }} data-testid={`${testidPrefix}-camera-retake`}>
              <RotateCcw size={16} style={{ marginRight: 6, verticalAlign: -3 }} /> Retake
            </button>
            <button type="button" className="btn-primary" onClick={confirm} style={{ height: 56, minWidth: 180, fontSize: 16, fontWeight: 800 }} data-testid={`${testidPrefix}-camera-confirm`}>
              <Check size={16} style={{ marginRight: 6, verticalAlign: -3 }} /> Use this photo
            </button>
          </>
        )}
      </div>
    </div>
  );
}
